import { Link, useNavigate } from '@tanstack/react-router'
import type { GroupCreate } from '@topmeeps/shared'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { useCreateGroup } from '../api/hooks'
import { ScreenShell } from '../components/ScreenShell'
import { BackButton, TopBar } from '../primitives'

export const GroupCreateScreen = () => {
  const navigate = useNavigate()
  const create = useCreateGroup()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Name is required')
      return
    }
    const payload: GroupCreate = {
      name: name.trim(),
      description: description.trim() ? description.trim() : null,
    }
    try {
      const group = await create.mutateAsync(payload)
      navigate({ to: '/group/$id', params: { id: group.id } })
    } catch (err) {
      if (err instanceof ApiError) setError(err.friendlyMessage)
      else setError(err instanceof Error ? err.message : 'unknown error')
    }
  }

  return (
    <ScreenShell>
      <TopBar left={<BackButton fallback={() => navigate({ to: '/home' })} />} title="New group" />

      <form
        onSubmit={onSubmit}
        style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tuesday Tabletops"
            maxLength={60}
            style={inputStyle}
            autoFocus
          />
        </Field>

        <Field label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Weekly meetup at the back booth. Optional."
            maxLength={500}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </Field>

        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          You'll be the first admin. Invite others from the group's settings once it's created.
        </div>

        {error && (
          <div
            style={{
              padding: 10,
              background: 'var(--terra-bg)',
              color: 'var(--terra-ink)',
              border: '1px solid var(--terra)',
              borderRadius: 4,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Link
            to="/home"
            className="btn-ghost"
            style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}
          >
            Cancel
          </Link>
          <button
            type="submit"
            className="btn-stamp"
            disabled={create.isPending}
            style={{ flex: 2 }}
          >
            {create.isPending ? 'Creating…' : 'Create group'}
          </button>
        </div>
      </form>
    </ScreenShell>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--paper)',
  border: '1px solid var(--line-soft)',
  borderRadius: 4,
  fontFamily: 'var(--font-display)',
  fontSize: 14,
  color: 'var(--ink)',
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div className="kicker" style={{ fontSize: 11 }}>
      {label}
    </div>
    <div style={{ marginTop: 4 }}>{children}</div>
  </div>
)
